import React from 'react';
import styled from 'styled-components';
import img from "../assets/Images/patrick-tomasso-Oaqk7qqNh_c-unsplash.jpg";
import LogoComponent from "../subComponents/LogoComponent";
import SocialIcons from "../subComponents/SocialIcons";
import PowerButton from "../subComponents/PowerButton";
import { Blogs } from '../data/BlogData';
import FeatsComponents from './FeatsComponents';
import AnchorComponents from '../subComponents/Anchor';
import { useState, useEffect } from 'react';
import BigTitle from "../subComponents/BigTitle"
import {motion} from 'framer-motion'

const MainContainer = styled(motion.div)`
  background-image: url(${img});
  background-size: cover;
  background-repeat: no-repeat;
  background-attachment: fixed;
  background-position: center;
  width: 100%;
  min-height: 100vh;
  overflow-x: hidden; /* Prevent horizontal scrollbar */
  
  /* Custom horizontal scrollbar styling */
  &::-webkit-scrollbar {
    height: 8px;
    background: ${(props) => props.theme.body}; /* Track color */
  }
  &::-webkit-scrollbar-thumb {
    background: ${(props) => props.theme.text}; /* Thumb color */
    border-radius: 4px;
  }
  &::-webkit-scrollbar-thumb:hover {
    background: ${(props) => props.theme.text}; /* Thumb hover color */
    opacity: 0.8;
  }

  /* For Firefox */
  scrollbar-color: ${(props) => props.theme.text} ${(props) => props.theme.body};
  scrollbar-width: thin;

  /* Hide scrollbar for Chrome, Safari and Opera */
  &::-webkit-scrollbar {
    display: none;
  }
  
  /* Hide scrollbar for IE, Edge and Firefox */
  -ms-overflow-style: none;  /* IE and Edge */
  scrollbar-width: none;  /* Firefox */
  
  /* Large desktop screens */
  @media (min-width: 1600px) {
    background-size: cover;
    background-position: center center;
  }
  
  @media (max-width: 768px) {
    background-attachment: scroll; /* Fixed attachment can cause issues on mobile */
    background-position: center center;
    scroll-behavior: smooth;
    -webkit-overflow-scrolling: touch;
  }
  
  @media (max-width: 480px) {
    background-size: cover;
    background-position: center center;
    background-attachment: scroll;
    scroll-behavior: smooth;
    -webkit-overflow-scrolling: touch;
  }
  
  /* For iOS devices which have issues with fixed backgrounds */
  @supports (-webkit-overflow-scrolling: touch) {
    background-attachment: scroll;
    background-size: cover;
    background-position: center center;
    -webkit-overflow-scrolling: touch;
  }
`;

const Container = styled.div`
  background-color: ${props => `rgba(${props.theme.bodyRgba}, 0.8)`};
  width: 100%;
  min-height: 100vh;
  position: relative;
  padding-bottom: 5rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  box-sizing: border-box;
  overflow-x: hidden; /* Prevent horizontal scrollbar */
  
  /* Hide scrollbar for Chrome, Safari and Opera */
  &::-webkit-scrollbar {
    display: none;
  }
  
  /* Hide scrollbar for IE, Edge and Firefox */
  -ms-overflow-style: none;  /* IE and Edge */
  scrollbar-width: none;  /* Firefox */
  
  @media (max-width: 768px) {
    padding-bottom: 6rem;
    padding-right: 1rem; /* Add space for anchor */
    scroll-behavior: smooth;
    -webkit-overflow-scrolling: touch;
  }
  
  @media (max-width: 480px) {
    padding-bottom: 7rem;
    padding-right: 0.5rem; /* Adjust space for anchor */
    scroll-behavior: smooth;
    -webkit-overflow-scrolling: touch;
  }
`;

const Center = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding-top: 2rem;
  width: 100%;
  position: relative;
  flex-direction: column;
  margin-top: 3rem;
  
  @media (min-width: 1600px) {
    margin-top: 4rem;
    padding-top: 3rem;
  }
  
  @media (max-width: 768px) {
    padding-top: 3rem;
    padding-bottom: 2rem;
    padding-right: 1rem;
    margin-top: 2.5rem;
  }
  
  @media (max-width: 480px) {
    padding-top: 4rem;
    padding-bottom: 4rem;
    padding-right: 0.5rem;
    margin-top: 2rem;
  }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  grid-auto-rows: 25.5rem;
  grid-gap: 2.5rem;
  width: 90%;
  max-width: 1600px;
  padding: 1rem;
  margin: 0 auto;
  box-sizing: border-box;
  align-items: stretch;
  justify-items: stretch;
  
  /* Large desktop screens */
  @media (min-width: 1600px) {
    grid-template-columns: repeat(4, 1fr);
    grid-auto-rows: 27.5rem;
    max-width: 1800px;
    grid-gap: 3rem;
  }
  
  /* Medium desktop screens */
  @media (min-width: 1200px) and (max-width: 1599px) {
    grid-template-columns: repeat(3, 1fr);
    max-width: 1400px;
    grid-gap: 2.5rem;
  }
  
  /* Small desktop and tablet landscape */
  @media (min-width: 769px) and (max-width: 1199px) {
    grid-template-columns: repeat(2, 1fr);
    max-width: 1000px;
    margin-left: 3rem;
    grid-gap: 2rem;
  }
  
  /* Tablet portrait */
  @media (max-width: 768px) {
    grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
    margin: 0 auto;
    width: calc(100% - 5rem); /* Make space for anchor */
    padding: 1rem;
    grid-gap: 2rem;
  }

  /* Mobile landscape */
  @media (max-width: 600px) {
    grid-template-columns: 1fr;
    width: calc(100% - 4rem); /* Make space for anchor */
    padding: 0.5rem;
    grid-gap: 1.5rem;
    max-width: 400px;
  }

  /* Mobile portrait */
  @media (max-width: 480px) {
    grid-template-columns: 1fr;
    grid-auto-rows: 23rem;
    width: calc(100% - 3.5rem); /* Make space for anchor */
    max-width: 300px;
    padding: 0.5rem;
    grid-gap: 1.5rem;
    margin: 0 auto;
  }
`;

const SocialIconsWrapper = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  z-index: 10;
  
  @media (max-width: 768px) {
    position: fixed;
    bottom: 0;
    top: auto;
  }
  
  @media (max-width: 480px) {
    transform: scale(0.9);
    transform-origin: bottom left;
  }
`;

const BigTitleWrapper = styled.div`
  position: sticky;
  top: 0;
  width: 100%;
  z-index: 15;
  padding: 1rem 0;
  display: flex;
  justify-content: center;
  pointer-events: none;
  margin-bottom: 2rem;
  
  
  
`;

const LogoWrapper = styled.div`
  position: relative;
  z-index: 10;
  margin-bottom: 1rem;
  display: flex;
  justify-content: center;
  width: 100%;
  
  @media (max-width: 768px) {
    margin-bottom: 0.5rem;
  }
`;

const PowerButtonWrapper = styled.div`
  position: relative;
  z-index: 10;
  margin-bottom: 2rem;
  display: flex;
  justify-content: center;
  width: 100%;
  
  @media (max-width: 768px) {
    margin-bottom: 1.5rem;
  }
  
  @media (max-width: 480px) {
    margin-bottom: 1rem;
  }
`;

const HeaderWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  padding-top: 2rem;
  
  @media (max-width: 768px) {
    padding-top: 1.5rem;
  }
  
  @media (max-width: 480px) {
    padding-top: 1rem;
  }
`;

const container={
  hidden:{opacity:0},
  show:{
    opacity:1,

    transition:{
      staggerChildren:0.5,
      duration:0.5,
    }
  }
}

// Also add a global style to hide scrollbars on the body
const GlobalStyle = styled.div`
  /* Apply to the entire page */
  & {
    /* overflow: hidden;  Remove to allow scrolling */
  }
  
  /* Hide scrollbar for Chrome, Safari and Opera */
  &::-webkit-scrollbar {
    display: none;
  }
  
  /* Hide scrollbar for IE, Edge and Firefox */
  -ms-overflow-style: none;  /* IE and Edge */
  scrollbar-width: none;  /* Firefox */
  
  @media (max-width: 768px) {
    scroll-behavior: smooth;
    -webkit-overflow-scrolling: touch;
  }
  @media (max-width: 480px) {
    scroll-behavior: smooth;
    -webkit-overflow-scrolling: touch;
  }
`;

const FeatsPage = () => {
  const [numbers, setNumbers] = useState(0);
  const [key, setKey] = useState(Date.now()); // Add a key state to force re-render on refresh

  useEffect(() => {
    let num=(window.innerHeight-70)/30;
    setNumbers(parseInt(num));

    // Reset the key when the page is refreshed
    const handleBeforeUnload = () => {
      setKey(Date.now());
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    // Reset on component mount to ensure fresh state
    setKey(Date.now());

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []);
  
  return (
    <GlobalStyle>
      <MainContainer
        key={key}
        variants={container}
        initial='hidden'
        animate='show'
        exit={{
          opacity:0,transition:{duration:0.5}
        }}
      >
        <Container>
          <SocialIconsWrapper>
            <SocialIcons />
          </SocialIconsWrapper>
          <AnchorComponents numbers={numbers} page="feats"/>
          
          <Center>
            <HeaderWrapper>
              <LogoWrapper>
                <LogoComponent />
              </LogoWrapper>
              <PowerButtonWrapper>
                <PowerButton />
              </PowerButtonWrapper>
            </HeaderWrapper>
            
            <BigTitleWrapper>
              <BigTitle text="FEATS" />
            </BigTitleWrapper>
            
            <Grid>
              {Blogs.map(blog => (
                <FeatsComponents key={`${blog.id}-${key}`} blog={blog} />
              ))}
            </Grid>
          </Center>
        </Container>
      </MainContainer>
    </GlobalStyle>
  );
};

export default FeatsPage;